import * as React from 'react';

import {
    Metadata,
    ResourceRetentionPolicySummary,
    Submission,
    SubmissionRequestSummary,
    PortalTextCommon,
    PortalTextSubmissionDetails,
} from '../../Models/Api/strongbox.financialportal';

import { StringFormat } from '../../Utils/StringUtils';
import { formatStringMDYFullMonth, FormatDate } from '../../Utils/DateUtils';
import { getDeletionDisplay } from '../../Utils/RetentionUtils';
import { RequestSummary } from './RequestSummary';
import './DetailsList.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleCheck,
    faCircleExclamation,
    faClock,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/regular';

type Props = {
    loadedUserDisplayName?: string;
    submissionDetailsText?: PortalTextSubmissionDetails;
    uploadDate: string;
    submittedBy: string | undefined;
    submissionRequestSummary?: SubmissionRequestSummary;
    submission?: Submission;
    commonText?: PortalTextCommon;
    retentionPolicySummary?: ResourceRetentionPolicySummary;
    submissionMetadata?: Metadata[];
}

/**
 * The collection's details as a property list (label | value rows). Rendered both in the page's
 * Collection details sidebar and in the narrow-viewport details drawer.
 */
export const FileGroupSubmissionDetailsSummary: React.FC<Props> = (props): React.ReactElement => {
    const {
        commonText,
        loadedUserDisplayName,
        retentionPolicySummary,
        submission,
        submissionDetailsText,
        submittedBy,
        submissionMetadata,
        submissionRequestSummary,
    } = props;

    const verified = !!submission && !!submission.isVerifiedContent;

    const deletionRow = (): React.ReactElement => {
        const deletion = getDeletionDisplay(retentionPolicySummary);
        if (!deletion) {
            return (<></>);
        }

        const deleteDate = FormatDate(new Date(deletion.date), formatStringMDYFullMonth);

        // A deleted collection is not "scheduled for deletion": show only that it was deleted, across the row.
        if (deletion.kind === 'deleted') {
            return (
                <div className={'page-details-sidebar__item page-details-sidebar__item--wide'}>
                    <dt>{StringFormat(submissionDetailsText?.templateSubmissionAutomaticallyDeleted || '{0}', deleteDate)}</dt>
                    <dd hidden={true} />
                </div>
            );
        }

        return (
            <div className={'page-details-sidebar__item'}>
                <dt>{submissionDetailsText?.scheduledForDeletionOn || ''}</dt>
                <dd>
                    <span className={`cr-tag ${deletion.imminent ? 'cr-tag--danger' : 'cr-tag--caution'}`}>
                        <span className={'cr-tag__icon'}><FontAwesomeIcon icon={faClock} /></span>
                        {deleteDate}
                    </span>
                </dd>
            </div>
        );
    }

    return (
        <dl className={'page-details-sidebar__list'}>
            <div className={'page-details-sidebar__item'}>
                <dt>{submissionDetailsText?.status || 'Status'}</dt>
                <dd>
                    <span className={`cr-tag ${verified ? 'cr-tag--success' : 'cr-tag--caution'}`}>
                        <span className={'cr-tag__icon'}><FontAwesomeIcon icon={verified ? faCircleCheck : faCircleExclamation} /></span>
                        {verified ? (commonText?.verified || '') : (commonText?.unverified || '')}
                    </span>
                </dd>
            </div>
            {retentionPolicySummary && deletionRow()}
            {
                // If we have an email for who was the actual submitter of this submission (see the comments in
                // FileGroup for props.submittedBy) and from a high level the request came from a shareable link,
                // then we show the submitter's email.  If there's a request summary then it's a shareable link if
                // hasShareableLink is true.  If there's no request summary, it happened a long time ago and we
                // assume that if there's an email for who submitted it that it was in fact a shareable link.
                !!submittedBy &&
                ((!!submissionRequestSummary && submissionRequestSummary.hasShareableLink) ||
                    (!submissionRequestSummary)) &&
                (
                    <div className={'page-details-sidebar__item'}>
                        <dt>{submissionDetailsText?.submittersEmail || ''}</dt>
                        <dd>{submittedBy}</dd>
                    </div>
                )
            }
            <RequestSummary
                inList={true}
                requestSummary={submissionRequestSummary}
                loadedUserDisplayName={loadedUserDisplayName}
                submission={submission}
                submissionMetadata={submissionMetadata}
            />
        </dl>
    );
}
